import React, { useState, useEffect } from "react";
import { Paper } from "@material-ui/core";
import AssetsTable from "../components/AssetsTable";
import AssetsFilters from "../components/AssetsFilters";
import Loading from "../components/Loading";
import Layout from "../components/Layout";
import useAxios from "../hooks/useAxios";
import useDebounced from "../hooks/useDebounced";
import usePersistedState from "../hooks/usePersistedState";
import { TICKET_API_URL, VIEWS_API_URL } from "../environmentVariables";
import { PageProps, Views, Query } from "../helpers/interfaceHelper";
import useFilters from "../hooks/useFilters";
import useQuery from "../hooks/useQuery";
import useView from "../hooks/useView";

export interface Asset {
    instance: string;
    tag: string;
    client: string;
    location: string;
    hostname: string;
    type: string;
    state: string;
    vendor: string;
    product: string;
    warrantyExpiry: string;
    serialNumber: string;
    ipAddress: string;
    softwareVersion: string;
    agent: string;
    impact: string;
    url: string;
    lastModified: string;
    [key: string]: string;
}

interface Data {
    data: Asset[];
    skip: number;
    limit: number;
    total: number;
}

/*
 * Generates the actual page to display the problens themselves.
 */
const AssetsPage: React.FC<PageProps> = ({ setDarkMode, darkMode }) => {
    const [sortProperty, setSortProperty] = useState("tag");
    const [sortDirection, setSortDirection] = useState(1);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [filters, setFilter, resetFilter] = useFilters([]);
    const [search, setSearch] = useState("");
    const [chosenView, setChosenView] = useView();
    const [isLoading, setLoading] = useState(false);
    const [defaultView, setDefaultView] = usePersistedState("All Assets", "default-asset-view");

    const debouncedSearch = useDebounced(search, 750);

    //retrieve the views from the relevant API and then use the chosen option
    //to construct query parameters
    const [views, viewsLongLoading] = useAxios<Views>(`${VIEWS_API_URL}/AssetViews`);

    //when we receive the list of views from the API, we want to make sure the default is selected
    useEffect(() => {
        setChosenView(defaultView, views.views);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [views.views]);

    //each time the page is rendered, we want to generate the query to be used to retrieve data from the api
    const query: Query = useQuery({
        sortDirection,
        sortProperty,
        page,
        rowsPerPage,
        filters,
        search: debouncedSearch,
        view: chosenView,
        views: views.views
    });

    //each time the page is rendered, we want to set the page to page 0 if:
    //the filters, debounced search, or view values have been changed
    useEffect(() => {
        setPage(0);
    }, [filters, debouncedSearch, chosenView]);

    //we now use axios to send the query rather than feathers
    const [data, dataLongLoading] = useAxios<Data>(`${TICKET_API_URL}/Assets`, query, 60000);

    //as data is loaded, the loadingData state should be modified. If we are currently loading data,
    //we set the loading state in order to display the loading page
    useEffect(() => {
        setLoading(viewsLongLoading || dataLongLoading);
    }, [viewsLongLoading, dataLongLoading]);

    const paging = {
        total: data.total,
        perPage: data.limit,
        page,
        setPage,
        setRowsPerPage
    };

    const sorting = {
        sortProperty,
        sortDirection,
        setSort: (property: string) => {
            if (property === sortProperty) {
                setSortDirection(sortDirection * -1);
            } else {
                setSortProperty(property);
            }
        }
    };

    //generate the layout for the page, including the filters and the table of data.
    //pass through any required information such as dark modes, paging and sorting settings
    return (
        <>
            {isLoading && <Loading />}
            <Layout
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                drawer={
                    <AssetsFilters
                        setFilter={setFilter}
                        resetFilter={resetFilter}
                        dbViews={views.views}
                        view={chosenView}
                        setView={setChosenView}
                        setDefaultView={setDefaultView}
                        search={search}
                        setSearch={setSearch}
                        query={query}
                    />
                }
            >
                <Paper style={{ overflow: "auto" }}>
                    <AssetsTable assets={data.data} paging={paging} sorting={sorting} />
                </Paper>
            </Layout>
        </>
    );
};

export default AssetsPage;
